import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { Layer, Popup, Source } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { LangContext } from '../../contexts/langContext';
import { Persona } from '../Persona';
import {
  clusterCountLayer,
  clusterLayer,
  interactiveLayerIds,
  MY_CITY_SOURCE_ID,
  myCityLayer,
  myCityPulseLayer,
  PEOPLE_SOURCE_ID,
  SELECTED_SOURCE_ID,
  selectedCountLayer,
  selectedPointLayer,
  selectedPulseLayer,
  unclusteredCountLayer,
  unclusteredPointLayer,
} from './cityMapLayers';
import './CityMap.css';
import { Button } from '../UiKit/Button';

const MAP_STYLE = `${process.env.PUBLIC_URL || ''}/map/city-map-style.json`;

const MAP_SETTINGS = {
  scrollZoom: true,
  boxZoom: true,
  dragRotate: false,
  pitchWithRotate: false,
  dragPan: true,
  keyboard: true,
  doubleClickZoom: true,
  touchZoomRotate: true,
  touchPitch: false,
  minZoom: 0,
  maxZoom: 20,
  minPitch: 0,
  maxPitch: 85,
};

const getLocation = (user) =>
  user?.location
  || (user?.aggr && user.aggr.city && user.aggr.city.location)
  || null;

const isCoord = (n) => typeof n === 'number' && !Number.isNaN(n);

const getUserCity = (user) => user?.aggr?.geo || user?.aggr?.city;

const formatCityLabel = (cityData) => {
  if (!cityData) return '';
  if (typeof cityData === 'string') return cityData.trim();
  return [cityData.city, cityData.province, cityData.country].filter(Boolean).join(', ');
};

const buildGroups = (people = [], me, myCity) => {
  const groups = {};

  const ensureGroup = (cityId, lat, lon, cityLabel) => {
    if (!groups[cityId]) {
      groups[cityId] = { cityId, lat, lon, cityLabel: cityLabel || '', people: [], tokens: new Set(), hasMe: false };
    } else if (cityLabel && !groups[cityId].cityLabel) {
      groups[cityId].cityLabel = cityLabel;
    }
    return groups[cityId];
  };

  people.forEach((persona) => {
    const user = persona?.user;
    if (!user) return;
    const cityId = user.city_id;
    const loc = getLocation(user);
    if (!cityId || !loc || !isCoord(loc.lat) || !isCoord(loc.lon)) return;

    const g = ensureGroup(cityId, loc.lat, loc.lon, formatCityLabel(getUserCity(user)));
    const token = (persona.avatar && persona.avatar.token) || user.token;
    if (token && g.tokens.has(token)) return;
    if (token) g.tokens.add(token);
    g.people.push(persona);
  });

  if (me && myCity && myCity.id && myCity.location && isCoord(myCity.location.lat) && isCoord(myCity.location.lon)) {
    const g = ensureGroup(myCity.id, myCity.location.lat, myCity.location.lon, formatCityLabel(myCity));
    const myToken = me.avatar?.token || me.user?.token;
    if (!myToken || !g.tokens.has(myToken)) {
      if (myToken) g.tokens.add(myToken);
      g.people.unshift({ user: me.user, avatar: me.avatar });
    }
    g.hasMe = true;
  }

  return Object.values(groups);
};

const buildGeoJson = (groups = []) => ({
  type: 'FeatureCollection',
  features: groups.map((group) => ({
    type: 'Feature',
    properties: {
      cityId: group.cityId,
      city: group.cityLabel,
      cityLabel: group.cityLabel,
      count: group.people.length,
      hasMe: group.hasMe ? 1 : 0,
    },
    geometry: {
      type: 'Point',
      coordinates: [group.lon, group.lat],
    },
  })),
});

const buildMyCityGeoJson = (coords, cityId) => {
  if (!coords) {
    return { type: 'FeatureCollection', features: [] };
  }
  return {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: { cityId },
      geometry: {
        type: 'Point',
        coordinates: [coords.lon, coords.lat],
      },
    }],
  };
};

const WORLD_VIEW = {
  zoom: 1.5,
  bearing: 0,
  pitch: 50,
};

const CITY_VIEW = {
  zoom: 11,
  bearing: 0,
  pitch: 50,
};

const INTRO_FLY_DURATION = 1400;
const CITY_FLY_DURATION = 1200;
const MAP_LAYOUT_MS = 320;
const SELECTION_FLY_DURATION = 600;
const HOME_CENTER_EPS = 0.02;
const HOME_ZOOM_EPS = 1;

// When the user has no city: start over the USA and slowly drift east.
const NO_CITY_VIEW = {
  longitude: -98,
  latitude: 39,
  zoom: 4.8,
  bearing: 0,
  pitch: 0,
};
const AUTO_PAN_DEG_PER_SEC = 3;

// Hover preview only makes sense with a real pointer; skip it on touch devices.
const SUPPORTS_HOVER =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(hover: hover) and (pointer: fine)').matches
    : true;

const DISABLED_INTERACTIONS = {
  scrollZoom: false,
  boxZoom: false,
  dragRotate: false,
  dragPan: false,
  keyboard: false,
  doubleClickZoom: false,
  touchZoomRotate: false,
  touchPitch: false,
};

const collectPeopleFromFeatures = (features, groupsByCityId) => {
  const people = [];
  const tokens = new Set();
  const cityLabels = [];
  const cityIds = [];

  features.forEach((feature) => {
    const props = feature.properties || {};
    const cityId = props.cityId;
    if (cityId != null) cityIds.push(String(cityId));
    const group = groupsByCityId[String(cityId)];
    const label = props.cityLabel
      || props.city
      || group?.cityLabel
      || formatCityLabel(getUserCity(group?.people?.[0]?.user));
    if (label) cityLabels.push(label);
    if (!group) return;
    group.people.forEach((persona) => {
      const token = (persona.avatar && persona.avatar.token) || (persona.user && persona.user.token);
      if (token && tokens.has(token)) return;
      if (token) tokens.add(token);
      people.push(persona);
    });
  });

  if (!cityLabels.length && people.length) {
    people.forEach((persona) => {
      const label = formatCityLabel(getUserCity(persona.user));
      if (label) cityLabels.push(label);
    });
  }

  const uniqueCities = [...new Set(cityLabels)];
  let title = uniqueCities[0] || '';
  if (uniqueCities.length > 1) {
    title = uniqueCities.slice(0, 2).join(' ');
    if (uniqueCities.length > 2) title += ` +${uniqueCities.length - 2}`;
  }

  return { people, title, cities: uniqueCities, cityIds: [...new Set(cityIds)] };
};

export function CityMap({
  people = [],
  me,
  myCity,
  focusCity = null,
  compact = false,
  selectedCityIds = [],
  selectionCoordinates = null,
  onSelectCluster = () => void (0),
  onReady = () => void (0),
  onCenterMe = () => void (0),
}) {
  const { $ui } = React.useContext(LangContext);
  const mapRef = useRef(null);
  const mapLoadedRef = useRef(false);
  const introPlayedRef = useRef(false);
  const prevCityIdRef = useRef(null);
  const prevFocusIdRef = useRef(null);
  const hoverReqRef = useRef(0);
  const autoPanRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [showCenterBtn, setShowCenterBtn] = useState(false);
  const [hoverPreview, setHoverPreview] = useState(null);
  const groups = useMemo(() => buildGroups(people, me, myCity), [people, me, myCity]);
  const groupsByCityId = useMemo(
    () => Object.fromEntries(groups.map((group) => [String(group.cityId), group])),
    [groups]
  );
  const geojson = useMemo(() => buildGeoJson(groups), [groups]);
  const selectedGeoJson = useMemo(() => {
    if (!selectedCityIds.length) {
      return { type: 'FeatureCollection', features: [] };
    }
    const ids = new Set(selectedCityIds.map(String));
    return buildGeoJson(groups.filter((group) => ids.has(String(group.cityId))));
  }, [groups, selectedCityIds]);

  const myCityId = myCity?.id || me?.user?.city_id || null;
  const hasCity = Boolean(myCityId);

  const myCoords = useMemo(() => {
    if (!hasCity) return null;
    const loc = (myCity && myCity.location) || (me && getLocation(me.user));
    if (!loc || !isCoord(loc.lat) || !isCoord(loc.lon)) return null;
    return { lat: loc.lat, lon: loc.lon };
  }, [hasCity, me, myCity]);

  const myCityGeoJson = useMemo(
    () => buildMyCityGeoJson(myCoords, myCityId),
    [myCoords, myCityId]
  );

  // A city the user searched for just to look at (does not change their profile).
  // It takes priority over the user's own city as the map's focus target.
  const focusCoords = useMemo(() => {
    const loc = focusCity && focusCity.location;
    if (!loc || !isCoord(loc.lat) || !isCoord(loc.lon)) return null;
    return { lat: loc.lat, lon: loc.lon };
  }, [focusCity]);

  const targetCoords = focusCoords || myCoords;

  const initialViewState = useMemo(() => {
    if (targetCoords) {
      return {
        longitude: targetCoords.lon,
        latitude: targetCoords.lat,
        ...WORLD_VIEW,
      };
    }
    return { ...NO_CITY_VIEW };
  }, [targetCoords]);

  const flyToCoords = useCallback((coords, duration = CITY_FLY_DURATION) => {
    if (!coords || !mapRef.current) return;
    mapRef.current.flyTo({
      center: [coords.lon, coords.lat],
      ...CITY_VIEW,
      duration,
    });
  }, []);

  const flyToMyCity = useCallback((duration = CITY_FLY_DURATION) => {
    flyToCoords(myCoords, duration);
  }, [flyToCoords, myCoords]);

  const playIntroFly = useCallback(() => {
    if (introPlayedRef.current) return;
    const target = focusCoords || myCoords;
    if (!target) return;
    introPlayedRef.current = true;
    prevCityIdRef.current = myCityId;
    prevFocusIdRef.current = focusCity?.id ?? null;
    flyToCoords(target, INTRO_FLY_DURATION);
  }, [focusCoords, myCoords, myCityId, focusCity, flyToCoords]);

  const onMapLoad = useCallback(() => {
    mapLoadedRef.current = true;
    setMapReady(true);
    const map = mapRef.current?.getMap?.();
    if (map?.touchZoomRotate?.disableRotation) {
      // Keep pinch-to-zoom, but never let the map rotate on touch.
      map.touchZoomRotate.disableRotation();
    }
    playIntroFly();
    onReady();
  }, [playIntroFly, onReady]);

  // Auto-drift east (starting over the USA) while the user has no city set.
  const stopAutoPan = useCallback(() => {
    if (autoPanRef.current) {
      cancelAnimationFrame(autoPanRef.current);
      autoPanRef.current = null;
    }
  }, []);

  const startAutoPan = useCallback(() => {
    const map = mapRef.current?.getMap?.();
    if (!map) return;
    stopAutoPan();
    let last = performance.now();
    const step = (now) => {
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;
      const center = map.getCenter();
      let lng = center.lng + AUTO_PAN_DEG_PER_SEC * dt;
      if (lng > 180) lng -= 360;
      map.setCenter([lng, center.lat]);
      autoPanRef.current = requestAnimationFrame(step);
    };
    autoPanRef.current = requestAnimationFrame(step);
  }, [stopAutoPan]);

  useEffect(() => {
    if (!mapReady) return undefined;
    if (hasCity || compact || focusCoords) {
      stopAutoPan();
      return undefined;
    }
    startAutoPan();
    return stopAutoPan;
  }, [mapReady, hasCity, compact, focusCoords, startAutoPan, stopAutoPan]);

  useEffect(() => {
    if (!mapLoadedRef.current) return;
    playIntroFly();
  }, [playIntroFly]);

  // Fly to a newly picked "look at this city" target after the intro has played.
  useEffect(() => {
    if (!mapReady || !introPlayedRef.current) return;
    const focusId = focusCity?.id ?? null;
    if (prevFocusIdRef.current === focusId) return;
    prevFocusIdRef.current = focusId;
    if (focusCoords) {
      flyToCoords(focusCoords, CITY_FLY_DURATION);
    }
  }, [mapReady, focusCity, focusCoords, flyToCoords]);

  useEffect(() => {
    if (!myCityId || !myCoords || !mapLoadedRef.current) return;
    if (!introPlayedRef.current) return;
    if (prevCityIdRef.current === myCityId) return;
    prevCityIdRef.current = myCityId;
    // Don't yank the map back home while the user is looking at another city.
    if (!focusCity || focusCity.id === myCityId) {
      flyToMyCity(CITY_FLY_DURATION);
    }
  }, [myCityId, myCoords, focusCity, flyToMyCity]);

  const centerOnMe = useCallback(() => {
    // Reset the "look at this city" target back to the user's own city…
    onCenterMe();
    // …and always fly home, even if the target was already my city (e.g. panned away).
    flyToMyCity(CITY_FLY_DURATION);
  }, [onCenterMe, flyToMyCity]);

  const isNearMyCity = useCallback(() => {
    if (!myCoords || !mapRef.current) return true;
    const map = mapRef.current.getMap();
    const center = map.getCenter();
    const zoom = map.getZoom();
    return Math.abs(center.lng - myCoords.lon) < HOME_CENTER_EPS
      && Math.abs(center.lat - myCoords.lat) < HOME_CENTER_EPS
      && Math.abs(zoom - CITY_VIEW.zoom) < HOME_ZOOM_EPS;
  }, [myCoords]);

  const updateCenterBtn = useCallback(() => {
    if (!myCoords) {
      setShowCenterBtn(false);
      return;
    }
    setShowCenterBtn(!isNearMyCity());
  }, [myCoords, isNearMyCity]);

  const centerOnSelection = useCallback((coordinates, duration = SELECTION_FLY_DURATION) => {
    if (!coordinates || !mapRef.current) return;
    mapRef.current.easeTo({
      center: coordinates,
      duration,
    });
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    const timer = setTimeout(() => {
      mapRef.current?.resize();
      if (compact && selectionCoordinates) {
        centerOnSelection(selectionCoordinates);
      }
    }, MAP_LAYOUT_MS);
    return () => clearTimeout(timer);
  }, [compact, selectionCoordinates, centerOnSelection]);

  const notifyClusterSelection = useCallback((features, coordinates) => {
    const selection = collectPeopleFromFeatures(features, groupsByCityId);
    if (!selection.people.length) return;
    onSelectCluster({ ...selection, coordinates });
  }, [groupsByCityId, onSelectCluster]);

  const getSinglePersona = useCallback((feature) => {
    const cityId = feature?.properties?.cityId;
    if (cityId == null) return null;
    const group = groupsByCityId[String(cityId)];
    return group?.people.length === 1 ? group.people[0] : null;
  }, [groupsByCityId]);

  const clearHoverPreview = useCallback(() => {
    hoverReqRef.current += 1;
    setHoverPreview(null);
  }, []);

  const onMapMouseMove = useCallback(async (event) => {
    if (!SUPPORTS_HOVER || compact || !hasCity || !mapRef.current) {
      clearHoverPreview();
      return;
    }

    const features = event.features;
    if (!features?.length) {
      clearHoverPreview();
      return;
    }

    const pointFeature = features.find(
      (f) => f.properties?.cityId != null && f.properties?.point_count == null
    );
    const pointPersona = pointFeature && getSinglePersona(pointFeature);
    if (pointPersona) {
      setHoverPreview({ lngLat: event.lngLat, persona: pointPersona });
      return;
    }

    const myCityFeature = features.find(
      (f) => f.layer?.id === myCityLayer.id || f.layer?.id === myCityPulseLayer.id
    );
    if (myCityFeature && myCityId) {
      const persona = getSinglePersona({ properties: { cityId: myCityId } });
      if (persona) {
        setHoverPreview({ lngLat: event.lngLat, persona });
        return;
      }
    }

    const clusterFeature = features.find(
      (f) => f.properties?.cluster_id != null && f.properties?.point_count === 1
    );
    if (clusterFeature) {
      const reqId = ++hoverReqRef.current;
      const source = mapRef.current.getMap().getSource(PEOPLE_SOURCE_ID);
      if (!source?.getClusterLeaves) {
        clearHoverPreview();
        return;
      }
      try {
        const leaves = await source.getClusterLeaves(clusterFeature.properties.cluster_id, 1, 0);
        if (reqId !== hoverReqRef.current) return;
        const persona = leaves?.[0] && getSinglePersona(leaves[0]);
        if (persona) {
          setHoverPreview({ lngLat: event.lngLat, persona });
          return;
        }
      } catch {
        // ignore
      }
    }

    clearHoverPreview();
  }, [compact, hasCity, getSinglePersona, myCityId, clearHoverPreview]);

  const onMapClick = useCallback(async (event) => {
    clearHoverPreview();
    if (!hasCity) return;
    const features = event.features;
    if (!features?.length || !mapRef.current) return;

    const map = mapRef.current.getMap();
    const myCityFeature = features.find(
      (f) => f.layer?.id === myCityLayer.id || f.layer?.id === myCityPulseLayer.id
    );
    if (myCityFeature && myCityId) {
      const group = groupsByCityId[String(myCityId)];
      if (group?.people.length) {
        notifyClusterSelection(
          [{
            properties: {
              cityId: myCityId,
              cityLabel: group.cityLabel,
              city: group.cityLabel,
            },
          }],
          myCityFeature.geometry.coordinates
        );
      }
      return;
    }

    const clusterFeature = features.find(
      (f) => f.layer?.id === clusterLayer.id && f.properties?.cluster_id != null
    ) || features.find((f) => f.properties?.cluster_id != null);
    const pointFeature = features.find(
      (f) => f.properties?.cityId != null && f.properties?.point_count == null
    );

    if (clusterFeature) {
      const clusterId = clusterFeature.properties.cluster_id;
      const pointCount = clusterFeature.properties.point_count;
      const source = map.getSource(PEOPLE_SOURCE_ID);
      if (!source?.getClusterLeaves) return;

      try {
        const leaves = await source.getClusterLeaves(clusterId, pointCount, 0);
        if (!leaves?.length) return;
        notifyClusterSelection(leaves, clusterFeature.geometry.coordinates);
      } catch {
        // cluster lookup failed — ignore click
      }
      return;
    }

    const feature = pointFeature || features[0];
    if (feature?.properties?.cityId == null) return;
    notifyClusterSelection([feature], feature.geometry.coordinates);
  }, [hasCity, notifyClusterSelection, groupsByCityId, myCityId, clearHoverPreview]);

  return (
    <div className={`city-map${compact ? ' city-map--compact' : ''}`}>
      {!compact && myCoords && hasCity && (
        <Button
          type="button"
          className="city-map__center-btn"
          onClick={centerOnMe}
          title={$ui('map_center_on_me')}
          icon="my_location"
          disabled={!showCenterBtn}
          ghost={!showCenterBtn}
          lg
        >
          {/* {$ui('map_center_on_me')} */}
        </Button>
      )}

      <Map
        ref={mapRef}
        initialViewState={initialViewState}
        mapStyle={MAP_STYLE}
        style={{ width: '100%', height: '100%' }}
        attributionControl={true}
        interactiveLayerIds={interactiveLayerIds}
        onClick={onMapClick}
        onMouseMove={onMapMouseMove}
        onMouseLeave={clearHoverPreview}
        onLoad={onMapLoad}
        onMoveEnd={updateCenterBtn}
        cursor={hasCity ? 'pointer' : 'default'}
        {...MAP_SETTINGS}
        {...(hasCity ? null : DISABLED_INTERACTIONS)}
      >
        <Source
          id={PEOPLE_SOURCE_ID}
          type="geojson"
          data={geojson}
          cluster
          clusterMaxZoom={14}
          clusterRadius={50}
        >
          <Layer {...clusterLayer} />
          <Layer {...clusterCountLayer} />
          <Layer {...unclusteredPointLayer} />
          <Layer {...unclusteredCountLayer} />
        </Source>

        {selectedCityIds.length > 0 && (
          <Source id={SELECTED_SOURCE_ID} type="geojson" data={selectedGeoJson}>
            <Layer {...selectedPulseLayer} />
            <Layer {...selectedPointLayer} />
            <Layer {...selectedCountLayer} />
          </Source>
        )}

        {myCoords && (
          <Source id={MY_CITY_SOURCE_ID} type="geojson" data={myCityGeoJson}>
            <Layer {...myCityPulseLayer} />
            <Layer {...myCityLayer} />
          </Source>
        )}

        {hoverPreview && (
          <Popup
            longitude={hoverPreview.lngLat.lng}
            latitude={hoverPreview.lngLat.lat}
            closeButton={false}
            closeOnClick={false}
            anchor="bottom"
            offset={16}
            className="city-map__persona-popup"
          >
            <Persona
              user={hoverPreview.persona.user}
              avatar={hoverPreview.persona.avatar}
              listId="map-hover"
              actions={[]}
              noClick
              addClassName="people"
              addLine="none"
              mode="hd"
            />
          </Popup>
        )}
      </Map>
    </div>
  );
}

export default CityMap;
